import { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Button,
    Autocomplete,
    AutocompleteItem,
    NumberInput,
    Tooltip,
    Chip,
} from "@heroui/react";
import {
    LuPlus,
    LuTrash2,
    LuStar,
    LuInfo,
    LuArrowRightLeft,
} from "react-icons/lu";
import { ThemedText } from "@/components/ui";
import { findOptionTitle } from "@/utils";

/**
 * PricingStep - Step 2: Manage multiple unit-price combinations
 */
const PricingStep = ({
    prices = [],
    onChange,
    units = [],
    isLoadingUnits = false,
    productName,
}) => {
    const [localPrices, setLocalPrices] = useState(prices);

    useEffect(() => {
        setLocalPrices(prices);
    }, [prices]);

    const updatePrices = (newPrices) => {
        setLocalPrices(newPrices);
        onChange?.(newPrices);
    };

    const handleAdd = () => {
        const isFirst = localPrices.length === 0;
        const newPrice = {
            material_unit_id: "",
            price: null, // null = empty input, not 0
            conversion_factor: isFirst ? 1 : null,
            is_default: isFirst,
        };
        updatePrices([...localPrices, newPrice]);
    };

    const handleRemove = (index) => {
        const newPrices = localPrices.filter((_, i) => i !== index);
        if (localPrices[index].is_default && newPrices.length > 0) {
            newPrices[0].is_default = true;
        }
        updatePrices(newPrices);
    };

    const handleChange = (index, field, value) => {
        const newPrices = [...localPrices];
        // Ensure conversion factor is 1 if default
        if (field === "is_default" && value === true) {
            newPrices.forEach((p, i) => {
                if (i !== index) p.is_default = false;
            });
            newPrices[index] = {
                ...newPrices[index],
                is_default: true,
                conversion_factor: 1, // Default unit base is always 1
            };
        } else {
            newPrices[index] = { ...newPrices[index], [field]: value };
        }
        updatePrices(newPrices);
    };

    const usedUnitIds = localPrices
        .map((p) => String(p.material_unit_id))
        .filter(Boolean);

    const getUnitName = (unitId) => findOptionTitle(units, unitId, "Satuan");

    const defaultUnit = localPrices.find((p) => p.is_default);
    const defaultUnitName = defaultUnit
        ? getUnitName(defaultUnit.material_unit_id)
        : "Base Unit";

    return (
        <div className="flex flex-col gap-5">
            {/* Header & Info */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <ThemedText size="sm">Daftar Harga & Satuan</ThemedText>
                        <Tooltip
                            content={
                                <div className="max-w-xs p-2">
                                    <p className="mb-2 font-medium">Tips:</p>
                                    <ul className="list-disc space-y-1 pl-4 text-xs">
                                        <li>
                                            Tentukan setidaknya satu satuan &
                                            harga.
                                        </li>
                                        <li>
                                            <LuStar
                                                className="text-warning-500 -mt-1 inline"
                                                size={12}
                                            />{" "}
                                            menandakan satuan dasar (default).
                                        </li>
                                        <li>
                                            Faktor konversi adalah nilai tukar
                                            terhadap satuan dasar.
                                        </li>
                                    </ul>
                                </div>
                            }
                        >
                            <div className="text-default-400 cursor-help">
                                <LuInfo size={18} />
                            </div>
                        </Tooltip>
                    </div>
                    <ThemedText variant="caption" size="sm">
                        Kelola variasi satuan jual untuk{" "}
                        <span className="font-semibold">
                            {productName || "Produk ini"}
                        </span>
                        .
                    </ThemedText>
                </div>
                <Button
                    size="sm"
                    color="primary"
                    startContent={<LuPlus size={16} />}
                    onPress={handleAdd}
                >
                    Tambah Satuan
                </Button>
            </div>

            {/* Price Rows */}
            <div className="flex flex-col gap-3">
                {localPrices.length === 0 ? (
                    <EmptyState onAdd={handleAdd} />
                ) : (
                    localPrices.map((price, index) => (
                        <PriceRow
                            key={index}
                            index={index}
                            price={price}
                            units={units}
                            usedUnitIds={usedUnitIds}
                            defaultUnitName={defaultUnitName}
                            isLoadingUnits={isLoadingUnits}
                            onChange={(field, value) =>
                                handleChange(index, field, value)
                            }
                            onRemove={() => handleRemove(index)}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

// Sub-component for individual price row to keep code clean
const PriceRow = ({
    index,
    price,
    units,
    usedUnitIds,
    defaultUnitName,
    isLoadingUnits,
    onChange,
    onRemove,
}) => {
    const isDefault = price.is_default;

    return (
        <Card
            className={`transition-all ${
                isDefault
                    ? "border-primary-200 bg-primary-50/10 dark:border-primary-900"
                    : "border-default-200 hover:border-default-300"
            } border`}
            shadow="none"
        >
            <CardBody className="gap-4 p-4">
                {/* Header: Numbering & Actions */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="bg-default-100 text-default-600 flex h-6 min-w-[24px] items-center justify-center rounded-md px-2 text-xs font-semibold">
                            #{index + 1}
                        </div>
                        {isDefault && (
                            <Chip
                                color="primary"
                                size="sm"
                                variant="flat"
                                startContent={<LuStar size={12} />}
                                className="h-6 gap-1 px-1"
                            >
                                Default
                            </Chip>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <Tooltip
                            content={
                                isDefault
                                    ? "Satuan ini adalah default"
                                    : "Jadikan satuan default"
                            }
                        >
                            <Button
                                isIconOnly
                                size="sm"
                                variant={isDefault ? "solid" : "light"}
                                color={isDefault ? "primary" : "default"}
                                onPress={() =>
                                    !isDefault && onChange("is_default", true)
                                }
                            >
                                <LuStar size={16} />
                            </Button>
                        </Tooltip>
                        <Tooltip content="Hapus satuan" color="danger">
                            <Button
                                isIconOnly
                                color="danger"
                                variant="light"
                                size="sm"
                                onPress={onRemove}
                            >
                                <LuTrash2 size={16} />
                            </Button>
                        </Tooltip>
                    </div>
                </div>

                {/* Inputs Container */}
                <div className="flex flex-col gap-4">
                    {/* Row 1: Satuan (Full Width) */}
                    <div className="w-full">
                        <Autocomplete
                            label="Satuan"
                            placeholder="Pilih satuan"
                            variant="bordered"
                            isLoading={isLoadingUnits}
                            defaultItems={units || []}
                            selectedKey={
                                price.material_unit_id
                                    ? String(price.material_unit_id)
                                    : null
                            }
                            onSelectionChange={(key) =>
                                onChange("material_unit_id", key)
                            }
                            disabledKeys={usedUnitIds.filter(
                                (id) => id !== String(price.material_unit_id),
                            )}
                            isRequired
                        >
                            {(item) => (
                                <AutocompleteItem
                                    key={String(item.value)}
                                    textValue={item.title}
                                >
                                    {item.title}
                                </AutocompleteItem>
                            )}
                        </Autocomplete>
                    </div>

                    {/* Row 2: Harga & Konversi */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <NumberInput
                            label="Harga Jual"
                            variant="bordered"
                            placeholder="0"
                            value={price.price ?? undefined}
                            onValueChange={(val) => onChange("price", val)}
                            startContent={
                                <ThemedText size="sm" variant="secondary">
                                    Rp
                                </ThemedText>
                            }
                            formatOptions={{
                                style: "decimal",
                                maximumFractionDigits: 0,
                            }}
                            hideStepper
                            minValue={0}
                            isRequired
                        />

                        <NumberInput
                            label="Faktor Konversi"
                            placeholder="Contoh: 2"
                            variant="bordered"
                            value={price.conversion_factor ?? undefined}
                            onValueChange={(val) =>
                                onChange("conversion_factor", val)
                            }
                            minValue={0.0001}
                            isDisabled={isDefault}
                            hideStepper
                            description={
                                price.material_unit_id && !isDefault ? (
                                    <span className="text-default-500 text-xs">
                                        1{" "}
                                        {units?.find(
                                            (u) =>
                                                String(u.value) ===
                                                String(price.material_unit_id),
                                        )?.title || "Unit"}{" "}
                                        = {price.conversion_factor || 1}{" "}
                                        {defaultUnitName}
                                    </span>
                                ) : undefined
                            }
                        />
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

const EmptyState = ({ onAdd }) => (
    <div className="border-default-200 bg-default-50 flex flex-col items-center justify-center rounded-lg border-2 border-dashed py-10 text-center">
        <div className="bg-default-200 mb-3 rounded-full p-3">
            <LuArrowRightLeft className="text-default-500" size={24} />
        </div>
        <h3 className="text-medium text-default-700 font-medium">
            Belum ada satuan harga
        </h3>
        <p className="text-small text-default-500 mb-6 max-w-xs">
            Tambahkan satuan untuk produk ini (misal: Pcs, Box, Kg) agar stok
            keluar masuk lebih teratur.
        </p>
        <Button
            size="sm"
            color="primary"
            variant="flat"
            onPress={onAdd}
            startContent={<LuPlus />}
        >
            Mulai Tambah Satuan
        </Button>
    </div>
);

export default PricingStep;
